// src/pages/usuarios/UsuariosPage.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userAdminApi } from "../../api/userAdminApi";
import {
  AdminUser,
  PageResponse,
  PaymentStatus,
  UserStatusFilter,
} from "../../types";
import { getErrorMessage } from "../../utils/errors";
import { formatDate, formatPhone, fullName } from "../../utils/format";
import { useDebounce } from "../../hooks/useDebounce";
import { useFlash } from "../../components/ui/Alert";
import {
  AccountBadge,
  PlanBadge,
  PLAN_LABEL,
} from "../../components/ui/Badges";
import Pagination from "../../components/ui/Pagination";
import UserAvatar from "../../components/ui/UserAvatar";
import {
  UserAction,
  UserActionModal,
} from "../../components/usuarios/UserActionModals";

const PAGE_SIZE = 20;

export default function UsuariosPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const flash = useFlash();

  // Los filtros viven en la URL para conservarlos al volver del detalle
  const status = (params.get("status") ?? "") as UserStatusFilter;
  const plan = (params.get("plan") ?? "") as PaymentStatus | "";
  const page = Number(params.get("page") ?? 0) || 0;
  const [search, setSearch] = useState(params.get("q") ?? "");
  const debouncedSearch = useDebounce(search);

  const [data, setData] = useState<PageResponse<AdminUser> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<{
    user: AdminUser;
    action: UserAction;
  } | null>(null);

  const updateParams = useCallback(
    (changes: Record<string, string | number | undefined>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(changes).forEach(([k, v]) => {
            if (v === undefined || v === "" || v === 0) next.delete(k);
            else next.set(k, String(v));
          });
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Al cambiar el texto buscado se vuelve a la primera página
  useEffect(() => {
    if ((params.get("q") ?? "") !== debouncedSearch) {
      updateParams({ q: debouncedSearch, page: undefined });
    }
  }, [debouncedSearch, params, updateParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAdminApi.listar({
        search: params.get("q") ?? "",
        status,
        plan,
        page,
        size: PAGE_SIZE,
      });
      setData(res.content);
    } catch (err) {
      flash.show(
        "error",
        getErrorMessage(err, "No se pudieron cargar los usuarios."),
      );
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const onActionDone = (updated: AdminUser, message: string) => {
    setSelected(null);
    flash.show("success", message);
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((u) => (u.id === updated.id ? updated : u)),
          }
        : prev,
    );
  };

  const hasFilters = !!(search || status || plan);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          <i
            className="bi bi-people-fill me-2"
            style={{ color: "var(--tp-green)", fontSize: "1.2rem" }}
          />
          Usuarios
        </h1>
        <p className="page-subtitle">
          Activa o desactiva cuentas y asigna cupones a usuarios específicos de
          la app Toinup
        </p>
      </div>

      {flash.node}

      <div className="tp-card">
        <div className="tp-card-header">
          <div className="filter-bar w-100">
            <div className="search-box">
              <i className="bi bi-search" />
              <input
                type="search"
                className="form-control"
                placeholder="Buscar por nombre, usuario, email, celular o identificación"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="form-select"
              value={status}
              onChange={(e) =>
                updateParams({ status: e.target.value, page: undefined })
              }
              aria-label="Estado de la cuenta"
            >
              <option value="">Todas las cuentas</option>
              <option value="ACTIVOS">Cuentas activas</option>
              <option value="INACTIVOS">Cuentas inactivas</option>
            </select>
            <select
              className="form-select"
              value={plan}
              onChange={(e) =>
                updateParams({ plan: e.target.value, page: undefined })
              }
              aria-label="Plan"
            >
              <option value="">Todos los planes</option>
              {(Object.keys(PLAN_LABEL) as PaymentStatus[]).map((p) => (
                <option key={p} value={p}>
                  {PLAN_LABEL[p]}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                className="btn-tp-ghost"
                onClick={() => {
                  setSearch("");
                  setParams({}, { replace: true });
                }}
              >
                <i className="bi bi-x-circle" /> Limpiar
              </button>
            )}
          </div>
        </div>

        {loading && !data ? (
          <div className="loading-state">
            <div className="spinner-border" />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-person-x" />
            {hasFilters
              ? "No hay usuarios que coincidan con los filtros"
              : "Aún no hay usuarios registrados"}
          </div>
        ) : (
          <>
            <div
              className="table-responsive"
              style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}
            >
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Celular</th>
                    <th>Registro</th>
                    <th>Plan</th>
                    <th>Cuenta</th>
                    <th style={{ textAlign: "right" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div className="cell-user">
                          <UserAvatar
                            imagePath={u.imagePath}
                            firstName={u.firstName}
                            lastName={u.lastName}
                            size="sm"
                          />
                          <div style={{ minWidth: 0 }}>
                            <div className="name">
                              {fullName(u.firstName, u.lastName, u.username)}
                              {u.admin && (
                                <i
                                  className="bi bi-shield-fill-check ms-1"
                                  style={{ color: "var(--tp-green)" }}
                                  title="Administrador"
                                />
                              )}
                            </div>
                            <div className="email">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatPhone(u.countryCode, u.phoneNumber)}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatDate(u.dateRegister)}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <PlanBadge status={u.paymentStatus} />
                        {u.subscriptionExpirationDate && (
                          <div className="text-muted-tp mt-1">
                            {u.remainingDays && u.remainingDays > 0
                              ? `${u.remainingDays} días · vence ${formatDate(u.subscriptionExpirationDate)}`
                              : `Venció ${formatDate(u.subscriptionExpirationDate)}`}
                          </div>
                        )}
                      </td>
                      <td>
                        <AccountBadge enabled={u.enabled} />
                      </td>
                      <td>
                        <div className="table-action-group">
                          <button
                            className="btn-tp-ghost"
                            onClick={() => navigate(`/usuarios/${u.id}`)}
                            title="Ver detalle"
                          >
                            <i className="bi bi-eye" />
                          </button>
                          <button
                            className="btn-tp-ghost"
                            style={{ color: "var(--tp-morado)" }}
                            onClick={() =>
                              setSelected({ user: u, action: "coupon" })
                            }
                            title="Asignar cupón"
                          >
                            <i className="bi bi-ticket-perforated" />
                          </button>
                          {u.enabled ? (
                            <button
                              className="btn-tp-ghost"
                              style={{ color: "var(--tp-red)" }}
                              onClick={() =>
                                setSelected({ user: u, action: "deactivate" })
                              }
                              title="Desactivar cuenta"
                            >
                              <i className="bi bi-slash-circle" />
                            </button>
                          ) : (
                            <button
                              className="btn-tp-success"
                              onClick={() =>
                                setSelected({ user: u, action: "activate" })
                              }
                              title="Activar cuenta"
                            >
                              <i className="bi bi-check-lg" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              totalElements={data.totalElements}
              size={data.size}
              onChange={(p) => updateParams({ page: p })}
            />
          </>
        )}
      </div>

      {selected && (
        <UserActionModal
          action={selected.action}
          user={selected.user}
          onClose={() => setSelected(null)}
          onDone={onActionDone}
        />
      )}
    </>
  );
}
